/**
 * What a counter dropdown can hold, in one place: a counter of this car, or a free-text duty ("SOS DCAR",
 * "B@M CTRL BOOTH" — see utils/dutyOptions). Every dropdown that assigns an officer to a counter goes
 * through components/CounterChoiceSelect, which is built on this, so they all offer the same options,
 * in the same groups, drawn the same way.
 *
 * A choice travels as a string: a 1-based counter number ("12"), "duty:<text>" for a duty, or ""
 * for none yet.
 */

/** The group headings: counters first, then everything that is not one. */
export const COUNTER_GROUP_LABEL = 'Counters';
/**
 * What the free-text duties are called, everywhere: the dropdown's group heading, the Settings section, the
 * counter page's label. One name, one place.
 */
export const NON_COUNTER_LABEL = 'Non-counter deployments';
export const DUTY_GROUP_LABEL = NON_COUNTER_LABEL;
/** The short form for the counter page's narrow label column. */
export const NON_COUNTER_SHORT_LABEL = 'Non-ctr';

/** The gray swatch's name in the grids' colour legends. */
export const NON_COUNTER_LEGEND_LABEL = 'Non-counter';

/** What choosing one does — the same lines wherever they are explained. */
export const NON_COUNTER_EFFECTS = [
  'Excluded from counter stats',
  'Excluded from No Relief notifications',
] as const;

/** The heading rows of a dropdown list — gray, so a group reads as a header and no option reads as chosen. */
export const GROUP_HEADER_LOOK = { background: '#c5cad2', color: '#1f2937', fontWeight: 700 } as const;

/**
 * The fill of a free-text duty wherever it is drawn: a solid gray, darker than the light gray of an empty
 * cell (#f0f0f0). Solid on purpose — a hatch reads as "counter unavailable" on the grids.
 */
export const DUTY_FILL = '#c5cad2';
export const DUTY_INK = '#1f2937';

/** How a free-text duty is drawn wherever it is chosen or shown: italic on that solid gray — never a counter's look. */
export const DUTY_LOOK = {
  fontStyle: 'italic',
  background: DUTY_FILL,
  border: '1.5px solid #64748b',
  color: DUTY_INK,
} as const;

/** How a dropdown still waiting for a choice is drawn. */
export const EMPTY_CHOICE_LOOK = { border: '1.5px solid #f59e0b', color: '#b45309' } as const;

export const dutyChoice = (text: string): string => `duty:${text}`;

export interface CounterChoice {
  /** The 1-based counter number; 0 for a duty or no choice. */
  counter: number;
  /** The duty text, when the choice is a duty. */
  duty?: string;
}

export function parseCounterChoice(choice: string): CounterChoice {
  if (choice.startsWith('duty:')) return { counter: 0, duty: choice.slice(5) };
  const counter = parseInt(choice, 10);
  return { counter: Number.isFinite(counter) ? counter : 0 };
}

/** The choice a row holds: its counter, or its duty (rows keep a duty with counter "0"). */
export const choiceOf = (row: { counter: string; duty?: string }): string =>
  row.duty ? dutyChoice(row.duty) : row.counter;

/**
 * The duties to offer: the user's list, plus the one already chosen when it is not on the list
 * (a duty typed by someone else, or since removed from this user's list) so it still reads as itself.
 */
export function dutiesToOffer(listed: string[], current?: string): string[] {
  return current && !listed.includes(current) ? [current, ...listed] : listed;
}
